import { useEffect, useState } from "react";

type Lang = "en" | "cs";

const LABELS: Record<Lang, string> = {
  en: "🇬🇧 English",
  cs: "🇨🇿 Čeština",
};

/** Strip a leading /cs prefix to get the locale-agnostic path. */
function basePath(pathname: string): string {
  if (pathname === "/cs" || pathname.startsWith("/cs/")) {
    return pathname.slice(3) || "/";
  }
  return pathname;
}

function currentLang(pathname: string): Lang {
  return pathname === "/cs" || pathname.startsWith("/cs/") ? "cs" : "en";
}

/**
 * Language dropdown rendered in the header (head-navigation-start slot).
 *
 * Switches between the English and Czech versions of the current page by
 * adding/removing the `/cs` path prefix. Deliberately uses only the DOM
 * (window.location) instead of router/Zudoku hooks: the slot renders outside
 * the contexts those hooks need, so reaching for them throws. A full navigation
 * is perfectly fine for a language switch. The mounted guard keeps it off the
 * server prerender (no `window` there) and stable before hydration.
 */
export function LanguageSwitcher() {
  const [pathname, setPathname] = useState<string | null>(null);

  useEffect(() => {
    setPathname(window.location.pathname);
  }, []);

  if (pathname === null) {
    return <div className="h-9 w-[120px]" aria-hidden />;
  }

  const lang = currentLang(pathname);
  const base = basePath(pathname);

  const onChange = (next: Lang) => {
    if (next === lang) return;
    const target = next === "cs" ? `/cs${base === "/" ? "" : base}` : base;
    window.location.assign(target || "/");
  };

  return (
    <select
      aria-label="Language"
      value={lang}
      onChange={(e) => onChange(e.target.value as Lang)}
      className="h-9 cursor-pointer rounded-md bg-transparent px-2 text-sm font-medium text-foreground outline-none hover:bg-accent focus-visible:ring-1 focus-visible:ring-ring"
    >
      <option value="en">{LABELS.en}</option>
      <option value="cs">{LABELS.cs}</option>
    </select>
  );
}
